import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);
const IDLE_TIMEOUT_MS = 60 * 60 * 1000;   // 60 min of inactivity

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const idleRef = useRef(null);

  const doLogout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    sessionStorage.removeItem("kr_token");
    sessionStorage.removeItem("kr_user");
    setUser(null);
    window.location.href = "/login";
  };

  // Idle timer: auto-logout after IDLE_TIMEOUT_MS with no activity
  useEffect(() => {
    if (!user) return;
    const reset = () => {
      if (idleRef.current) clearTimeout(idleRef.current);
      idleRef.current = setTimeout(doLogout, IDLE_TIMEOUT_MS);
    };
    ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach(e =>
      window.addEventListener(e, reset, { passive: true })
    );
    reset();
    return () => {
      if (idleRef.current) clearTimeout(idleRef.current);
      ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach(e =>
        window.removeEventListener(e, reset)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        sessionStorage.removeItem("kr_token");
        sessionStorage.removeItem("kr_user");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Store token in sessionStorage (NOT localStorage) so it's cleared on tab close.
    if (data.token) {
      sessionStorage.setItem("kr_token", data.token);
      sessionStorage.setItem("kr_user", JSON.stringify(data.user));
    }
    setUser(data.user);
    return data.user;
  };

  const logout = async () => { await doLogout(); };

  const can = (screen) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    return (user.permissions || []).includes(screen);
  };

  /** CRUD-level check. Falls back to full CRUD when role has no explicit
   * actions map (back-compat). Admin bypasses everything. */
  const canDo = (screen, action = "read") => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!(user.permissions || []).includes(screen)) return false;
    const acts = user.actions || null;
    if (!acts) return true; // legacy user, allow
    const allowed = acts[screen];
    if (!allowed) return action === "read"; // has screen but no explicit action list = read only
    return allowed.includes(action);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, can, canDo }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
